import React, { useEffect, useRef, useState } from 'react';

// hls.js is not an installed package on this site: the existing player loads it in the browser from a CDN, and so does this one.
let hlsLoader = null;
function loadHls() {
  if (window.Hls) return Promise.resolve(window.Hls);
  if (!hlsLoader) {
    hlsLoader = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/hls.js/1.5.7/hls.min.js';
      s.onload = () => resolve(window.Hls);
      s.onerror = () => { hlsLoader = null; reject(new Error('hls.js did not load')); };
      document.head.appendChild(s);
    });
  }
  return hlsLoader;
}

// One signed link serves every preview for an hour, so clicking around does not flood the audit log with link requests.
// If a try fails and the link is not brand new, it is dropped, so the next try asks for a fresh one (e.g. after the API restarted).
let tokenCache = { t: null, at: 0 };
function getCachedToken(getToken) {
  if (tokenCache.t && Date.now() - tokenCache.at < 60 * 60 * 1000) return Promise.resolve(tokenCache.t);
  return Promise.resolve(getToken()).then(t => { tokenCache = { t: t, at: Date.now() }; return t; });
}
function dropOldToken() { if (Date.now() - tokenCache.at > 20000) tokenCache = { t: null, at: 0 }; }
export function _resetTokenCache() { tokenCache = { t: null, at: 0 }; }

// PREVIEW player for the Production page: it plays what a source WILL show, without putting anything on air.
//   pv = { kind: 'hls',  url, waitText }                    a live playlist (Radio, the DJ master)
//      | { kind: 'file', path, query, loop }                a file streamed by the API (a VOD file, the weather card)
//      | { kind: 'black' } | { kind: 'none', note }
// It starts muted (the program already carries the sound); the speaker button lets you listen in.
// The page gives it a key per source, so choosing another source always starts it fresh.
export default function PreviewPlayer({ pv, getToken }) {
  const kind = pv && pv.kind;
  const playable = kind === 'hls' || kind === 'file';
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [state, setState] = useState('loading');   // loading | playing | waiting | error
  const [attempt, setAttempt] = useState(0);
  const [copy, setCopy] = useState(null);       // VOD only: which version is being played (the low-data copy or the original)
  const [forceOrig, setForceOrig] = useState(false);   // VOD only: the low-data copy failed, so play the original
  const sig = JSON.stringify(pv || null);

  useEffect(() => { if (videoRef.current) videoRef.current.muted = muted; }, [muted, sig, attempt]);

  useEffect(() => {
    if (!playable) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;
    let dead = false, hls = null, retry = null;
    const fail = () => {
      if (dead) return;
      if (kind === 'file') dropOldToken();
      if (kind === 'file' && !pv.loop && attempt >= 3) { setState('error'); return; }   // a VOD file that will not play here: stop asking
      setState('waiting');
      clearTimeout(retry);
      retry = setTimeout(() => setAttempt(a => a + 1), 4000);                  // a live source (or the weather card) that is not there yet: keep trying
    };
    const onPlaying = () => { if (!dead) setState('playing'); };
    video.addEventListener('playing', onPlaying);
    setState('loading');
    (async () => {
      try {
        if (kind === 'file') {
          const t = await getCachedToken(getToken);
          if (dead) return;
          const q = new URLSearchParams(Object.assign({}, pv.query || {}, { t: t })).toString();
          // A VOD: ask which version is ready. A low-data copy (HLS, 360p) starts in seconds on a poor connection; otherwise the original.
          let useCopy = false;
          if (/\/vod$/.test(pv.path)) {
            let st = null;
            try {
              st = await Promise.race([
                fetch('/api' + pv.path + '/status?' + q).then(r => (r.ok ? r.json() : null)),
                new Promise(r => setTimeout(() => r(null), 2500)),
              ]);
            } catch (e) { st = null; }
            if (dead) return;
            setCopy(forceOrig && st ? { state: 'failed' } : st);
            useCopy = !forceOrig && !!st && st.state === 'ready';
          }
          video.loop = !!pv.loop;
          const toOriginal = () => { if (!dead) setForceOrig(true); };       // the copy did not work: start over with the original
          if (useCopy) {
            const url = '/api' + pv.path + '/hls.m3u8?' + q;
            let Hls = null;
            try { Hls = await loadHls(); } catch (e) { Hls = null; }
            if (dead) return;
            if (Hls && Hls.isSupported()) {
              hls = new Hls({ enableWorker: true, lowLatencyMode: false, maxBufferLength: 20, maxMaxBufferLength: 40, backBufferLength: 10, startPosition: 0 });
              hls.on(Hls.Events.ERROR, (e, d) => { if (d && d.fatal) { try { hls.destroy(); } catch (x) {} hls = null; toOriginal(); } });
              hls.on(Hls.Events.MANIFEST_PARSED, () => { video.play().catch(() => {}); });
              hls.loadSource(url);
              hls.attachMedia(video);
              return;
            }
            if (video.canPlayType('application/vnd.apple.mpegurl')) {
              video.onerror = toOriginal;
              video.src = url;
              video.play().catch(() => {});
              return;
            }
          }
          video.onerror = fail;
          video.src = '/api' + pv.path + '?' + q;
          video.play().catch(() => {});
        } else {
          let Hls = null;
          try { Hls = await loadHls(); } catch (e) { Hls = null; }
          if (dead) return;
          if (Hls && Hls.isSupported()) {
            hls = new Hls({ enableWorker: true, lowLatencyMode: false, liveSyncDurationCount: 3, maxBufferLength: 10, backBufferLength: 10 });
            hls.on(Hls.Events.ERROR, (e, d) => {
              if (d && d.fatal) { try { hls.destroy(); } catch (x) {} hls = null; fail(); }
            });
            hls.on(Hls.Events.MANIFEST_PARSED, () => { video.play().catch(() => {}); });
            hls.loadSource(pv.url);
            hls.attachMedia(video);
          } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
            video.onerror = fail;
            video.src = pv.url;
            video.play().catch(() => {});
          } else {
            fail();
          }
        }
      } catch (e) { fail(); }
    })();
    return () => {
      dead = true;
      clearTimeout(retry);
      video.removeEventListener('playing', onPlaying);
      video.onerror = null;
      if (hls) { try { hls.destroy(); } catch (x) {} }
      try { video.pause(); video.removeAttribute('src'); video.load(); } catch (x) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, attempt, forceOrig]);

  const note = (txt, sub) => (
    <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:'4px',pointerEvents:'none'}}>
      <div style={{fontSize:'10px',fontWeight:'800',letterSpacing:'2px',color:'rgba(255,255,255,0.55)',textAlign:'center',padding:'0 12px'}}>{txt}</div>
      {sub && <div style={{fontSize:'8px',letterSpacing:'1px',color:'rgba(255,255,255,0.3)'}}>{sub}</div>}
    </div>
  );

  if (kind === 'black') return <div style={{position:'absolute',inset:0,background:'#000'}}>{note('BLACK SLATE', 'no picture')}</div>;
  if (kind === 'none') return <div style={{position:'absolute',inset:0,background:'#050508'}}>{note((pv && pv.note) || 'NO SIGNAL')}</div>;

  const waitText = (pv && pv.waitText) || (kind === 'file' ? 'NOT READY YET' : 'NO SIGNAL');
  // Tells you which version of a VOD you are watching: the low-data copy looks softer than what goes on air.
  const badge = !copy ? null
    : copy.state === 'ready' ? { txt: 'LOW-DATA COPY · 360p', tip: 'A small copy for previewing on a poor connection. The picture is softer than what goes on air; the original is what airs.', ready: true }
    : copy.state === 'building' ? { txt: 'ORIGINAL · copy ' + (copy.pct || 0) + '%', tip: 'This is the full-quality file. A low-data copy is being built in the background.' }
    : copy.state === 'paused' ? { txt: 'ORIGINAL · copy paused', tip: 'The low-data copy is paused while a DJ show is live.' }
    : copy.state === 'queued' ? { txt: 'ORIGINAL · copy queued', tip: 'This is the full-quality file. A low-data copy will be built in the background.' }
    : { txt: 'ORIGINAL', tip: 'This is the full-quality file.' };
  return (
    <div style={{position:'absolute',inset:0,background:'#000'}}>
      <video ref={videoRef} muted playsInline preload="auto" controls={kind === 'file' && !pv.loop}
        style={{width:'100%',height:'100%',objectFit:'contain',background:'#000'}}/>
      {state === 'loading' && note('LOADING…')}
      {state === 'waiting' && note(waitText, 'retrying…')}
      {state === 'error' && note('CAN’T PREVIEW THIS FILE HERE', 'it can still go on air')}
      {badge && <div title={badge.tip} style={{position:'absolute',top:'18px',right:'7px',fontSize:'8px',fontWeight:'800',letterSpacing:'1px',padding:'2px 6px',borderRadius:'3px',
        background:'rgba(0,0,0,0.6)',color:badge.ready?'#55dd88':'rgba(255,255,255,0.6)',border:'1px solid '+(badge.ready?'rgba(0,200,80,0.4)':'rgba(255,255,255,0.15)')}}>{badge.txt}</div>}
      <button type="button" title={muted ? 'Listen to this preview' : 'Mute this preview'}
        onClick={e => { e.stopPropagation(); setMuted(m => !m); }}
        style={{position:'absolute',top:'5px',left:'7px',fontSize:'11px',lineHeight:1,padding:'3px 6px',borderRadius:'4px',cursor:'pointer',
          background:'rgba(0,0,0,0.6)',color:'#fff',border:'1px solid rgba(255,255,255,0.2)'}}>{muted ? '🔇' : '🔊'}</button>
    </div>
  );
}
